import { Link } from '@tanstack/react-router';
import * as React from 'react';
import styled from 'styled-components';

import * as icons from '@creditornot/alchemy-react/icons';
import { TextButton } from '@creditornot/cb-button';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { ApiErrorNotification } from 'common/components/cookbook/ApiErrorNotification';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { DetailItem } from 'common/components/cookbook/DetailItem';
import { Stack } from 'common/components/cookbook/Stack';
import { Typography as T } from 'common/components/cookbook/Typography/Typography';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

import { usePurchaseOrderDetailsQuery } from '../query';

const Root = styled(ContentContainer)`
  display: flex;
  flex-direction: column;
  gap: 24px;
  border-bottom: 1px solid ${color.border};
`;

export const ShipmentInformation: React.FC<{
  inboundLoadId: string;
  shipmentName?: string;
  expectedQuantity?: number;
}> = ({ inboundLoadId, shipmentName, expectedQuantity }) => {
  const formatMessage = useFormatMessage();

  const { data, isLoading, error } = usePurchaseOrderDetailsQuery(inboundLoadId);
  const { waybills } = data ?? {};

  return (
    <Root>
      <T.subheadline>{formatMessage('receiving.shipment_information')}</T.subheadline>
      <DetailItem label={formatMessage('common.purchase_order')}>
        {shipmentName && !isLoading ? (
          <Stack direction="column" gap="regularMedium">
            <T.caption data-test-id="shipment-name">{shipmentName}</T.caption>
            <Link
              to="/purchase-orders/$poId"
              params={{ poId: inboundLoadId }}
              search={{ waybillEditOpen: true }}
            >
              {waybills?.length ? (
                <TextButton size="small" variant="brand">
                  {formatMessage('receiving.waybills_count', { count: waybills?.length })}
                </TextButton>
              ) : (
                <TextButton size="small" variant="brand" iconPosition="start" icon={icons.plus}>
                  {formatMessage('receiving.add_waybill')}
                </TextButton>
              )}
            </Link>
          </Stack>
        ) : (
          formatMessage('common.not_available_acronym')
        )}
      </DetailItem>
      <ApiErrorNotification error={error} />
      <DetailItem
        label={formatMessage('receiving.expected_quantity')}
        data-test-id="expected-quantity"
      >
        {expectedQuantity ?? formatMessage('common.not_available_acronym')}
      </DetailItem>
    </Root>
  );
};
